import * as Sentry from "@sentry/react";
import type { EmbedHostRole } from "@/embed/embed-hosts";
import { stripQueryStrings } from "@/embed/embed-telemetry-scrub";
import {
  isChunkLoadSentryEvent,
  isChunkReloadFalloutSentryEvent,
} from "./chunk-load-error-handler.ts";

/**
 * Sentry Session Replay settings for the host the page is served on.
 *
 * The dashboard records text so a replay shows what the person saw, masking
 * only credential fields and `.ph-mask` secrets. Inside a Salesforce panel the
 * page shows a customer's Salesforce record context next to Upside data, so
 * the embed host masks every text node and input and blocks media: a replay
 * there keeps the clicks and layout, not the content. Its recorded events
 * (navigation and network entries) also lose every URL's query string, which
 * holds the record ID and the panel's link.
 */
export function sentryReplayOptions(
  hostKind: EmbedHostRole["kind"],
): Parameters<typeof Sentry.replayIntegration>[0] {
  if (hostKind === "embed") {
    return {
      maskAllText: true,
      maskAllInputs: true,
      blockAllMedia: true,
      beforeAddRecordingEvent: (event) => stripQueryStrings(event),
    };
  }
  return {
    maskAllText: false,
    maskAllInputs: false,
    mask: [
      'input[type="password"]',
      'input[type="email"]',
      'input[type="tel"]',
      // Reveal-once secrets (e.g. a freshly-minted personal API key) —
      // shares the `.ph-mask` class PostHog's session recording
      // already recognizes by convention, so one class masks both.
      ".ph-mask",
    ],
    // Blocked, not just masked: the element is replaced by a placeholder, so
    // neither its text nor any attribute (a `title`, an `aria-label`) is
    // recorded. `.sentry-block` is Sentry's own class for it; `.ph-no-capture`
    // is the class PostHog's recording and autocapture already skip. Customer
    // record keys and values (reverse ETL's `Sensitive`) carry both, so the
    // block holds whichever convention a later change keeps.
    block: [".sentry-block", ".ph-no-capture"],
  };
}

export interface SentryInitInputs {
  environment: string;
  /** Host that receives trace headers. */
  apiHost: string;
  hostKind: EmbedHostRole["kind"];
}

/**
 * *event* without URL queries, and without `request.query_string`, which
 * holds the page's query on its own (no `?` for the text scrub to find).
 */
function scrubEmbedEvent<T extends Sentry.Event>(event: T): T {
  const scrubbed = stripQueryStrings(event);
  if (scrubbed.request) {
    const { query_string: _dropped, ...request } = scrubbed.request;
    return { ...scrubbed, request };
  }
  return scrubbed;
}

/**
 * On the embed address, everything sent to Sentry loses the query string of
 * every URL in it: the page's address and its API calls carry the
 * Salesforce record ID and the panel's link (`embed-telemetry-scrub.ts`).
 */
function embedQueryScrubbing(): Partial<Sentry.BrowserOptions> {
  return {
    beforeSendTransaction: (event) => scrubEmbedEvent(event),
    beforeSendSpan: (span) => stripQueryStrings(span),
    beforeBreadcrumb: (breadcrumb) => stripQueryStrings(breadcrumb),
  };
}

export function sentryInitOptions({
  environment,
  apiHost,
  hostKind,
}: SentryInitInputs): Sentry.BrowserOptions {
  const embed = hostKind === "embed";
  return {
    ...(embed ? embedQueryScrubbing() : {}),
    dsn: "https://63a67126211ef53d0e1073931c7e3e6b@o4508780440715264.ingest.us.sentry.io/4508780551143424",
    environment,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(sentryReplayOptions(hostKind)),
    ],
    tracesSampleRate: 1.0,
    tracePropagationTargets: [apiHost],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event) {
      if (
        isChunkReloadFalloutSentryEvent(event) ||
        isChunkLoadSentryEvent(event)
      ) {
        return null;
      }
      return embed ? scrubEmbedEvent(event) : event;
    },
  };
}
